"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import { FEATURE_FLAG_KEYS, isAssistantClientEnabled } from "@agroproj/backend/convex/assistant/config";
import { useMutation, useQuery } from "convex/react";
import type { Route } from "next";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button, ButtonLink } from "@/components/ui/button";
import { Section } from "@/components/ui/section";
import { TextLineSkeleton } from "@/components/ui/skeleton";
import { useConvexFeatureFlag } from "@/hooks/use-convex-feature-flag";

/**
 * Loading "Pontos de atenção" with the empty-result layout: checked-at line, one result
 * line and the coverage line.
 */
function AssistantInsightsSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Section aria-busy="true" title={"Pontos de aten\xE7\xE3o"}><TextLineSkeleton className="w-80 max-w-full" /><TextLineSkeleton className="w-40" /><TextLineSkeleton className="w-56 max-w-full" /></Section>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * Whether the insights section may appear, mirroring the server's checks so the skeleton
 * only holds space where results can arrive. Unresolved flags count as possibly on.
 */
function useInsightsMayShow() {
  const $ = _c(3);
  const assistant = useConvexFeatureFlag(FEATURE_FLAG_KEYS.ASSISTANT);
  const insights = useConvexFeatureFlag(FEATURE_FLAG_KEYS.ASSISTANT_INSIGHTS);
  let t0;
  if ($[0] !== assistant || $[1] !== insights) {
    t0 = isAssistantClientEnabled() && (assistant.isLoading || assistant.enabled) && (insights.isLoading || insights.enabled);
    $[0] = assistant;
    $[1] = insights;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}

/** `pending` renders the loading state without querying, before Convex auth is ready. */
export function AssistantInsights(t0) {
  const $ = _c(36);
  const {
    organizationSlug,
    pending: t1
  } = t0;
  const pending = t1 === undefined ? false : t1;
  const mayShow = useInsightsMayShow();
  const clock = useMutation(api.assistant.insights.clock);
  const dismiss = useMutation(api.assistant.insights.dismiss);
  const [clockError, setClockError] = useState(false);
  const [bucket, setBucket] = useState();
  let t2;
  if ($[0] !== clock || $[1] !== pending) {
    t2 = () => {
      if (pending) {
        return;
      }
      let active = true;
      const refresh = () => {
        if (document.visibilityState === "visible") {
          clock({}).then(value => {
            if (active) {
              setBucket(value);
              setClockError(false);
            }
          }).catch(() => {
            if (active) {
              setClockError(true);
            }
          });
        }
      };
      refresh();
      const timer = setInterval(refresh, 60000);
      window.addEventListener("focus", refresh);
      document.addEventListener("visibilitychange", refresh);
      return () => {
        active = false;
        clearInterval(timer);
        window.removeEventListener("focus", refresh);
        document.removeEventListener("visibilitychange", refresh);
      };
    };
    $[0] = clock;
    $[1] = pending;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== clock || $[4] !== organizationSlug || $[5] !== pending) {
    t3 = [clock, organizationSlug, pending];
    $[3] = clock;
    $[4] = organizationSlug;
    $[5] = pending;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] !== bucket || $[8] !== organizationSlug || $[9] !== pending) {
    t4 = pending || bucket === undefined ? "skip" : {
      organizationSlug,
      bucket
    };
    $[7] = bucket;
    $[8] = organizationSlug;
    $[9] = pending;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const result = useQuery(api.assistant.insights.list, t4);
  if (clockError) {
    let t5;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Section title={"Pontos de aten\xE7\xE3o"}><p>Não foi possível atualizar os alertas. Tente novamente ao retornar à página.</p></Section>;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    return t5;
  }
  if (result === undefined) {
    let t5;
    if ($[12] !== mayShow) {
      t5 = mayShow ? <AssistantInsightsSkeleton /> : null;
      $[12] = mayShow;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  if (!result) {
    return null;
  }
  let t5;
  if ($[14] !== result.coverage) {
    t5 = result.coverage.some(_temp);
    $[14] = result.coverage;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const limited = t5;
  const t6 = limited ? "Sele\xE7\xE3o parcial dos registros consultados." : "At\xE9 cinco alertas dos registros consultados.";
  let t7;
  if ($[16] !== result.observedAt || $[17] !== result.timezone) {
    t7 = new Intl.DateTimeFormat("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: result.timezone
    }).format(result.observedAt);
    $[16] = result.observedAt;
    $[17] = result.timezone;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== result.timezone || $[20] !== t6 || $[21] !== t7) {
    t8 = <p className="text-muted-foreground text-xs/relaxed">{t6}{" "}Verificado às{" "}{t7}{" "}({result.timezone}).</p>;
    $[19] = result.timezone;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== dismiss || $[24] !== limited || $[25] !== organizationSlug || $[26] !== result.items) {
    t9 = result.items.length === 0 ? <p>Nenhum alerta encontrado{limited ? " nesta sele\xE7\xE3o" : ""}.</p> : <ul className="divide-y">{result.items.map(item => <li className="flex flex-wrap items-center justify-between gap-2 py-2" key={item.key}><p className="min-w-0 flex-1">{item.reason}</p><div className="flex gap-1"><ButtonLink href={item.path as Route} size="sm" variant="outline">Abrir</ButtonLink><Button size="sm" variant="ghost" onClick={() => {
            dismiss({
              organizationSlug,
              key: item.key,
              fingerprint: item.fingerprint
            }).catch(_temp2);
          }}>Ocultar por 7 dias</Button></div></li>)}</ul>;
    $[23] = dismiss;
    $[24] = limited;
    $[25] = organizationSlug;
    $[26] = result.items;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== result.coverage) {
    t10 = result.coverage.map(_temp3).join(" \xB7 ") || "Nenhum dom\xEDnio habilitado.";
    $[28] = result.coverage;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10) {
    t11 = <p className="text-muted-foreground text-xs/relaxed">{t10}</p>;
    $[30] = t10;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t11 || $[33] !== t8 || $[34] !== t9) {
    t12 = <Section title={"Pontos de aten\xE7\xE3o"}>{t8}{t9}{t11}</Section>;
    $[32] = t11;
    $[33] = t8;
    $[34] = t9;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  return t12;
}
function _temp3(scope_0) {
  return `${scope_0.domain === "projects" ? "Projetos" : scope_0.domain === "finance" ? "Financeiro" : "CRM"}: ${scope_0.scanned}${scope_0.limited ? " (leitura limitada)" : ""}`;
}
function _temp2() {
  return toast.error("N\xE3o foi poss\xEDvel ocultar o alerta.");
}
function _temp(scope) {
  return scope.limited;
}